/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {Metric, MetricsState, Record, User} from '@exc/metrics';
const re = /adobe_optout=/gi;

export default class Payload {
  public static body: {user: string[]; log: string[]} = {log: [], user: []};
  public static size = 0;
  private static payloadSequence = 1;

  public static next(): void {
    Payload.body = {log: [], user: []};
    ++Payload.payloadSequence;
    Payload.size = 0;
  }

  public static finalizeRecord(record: Record, state: MetricsState): void {
    record.metricsState = Object.assign({}, state, {
      application: (global as any).adobeMetrics.application,
      deviceId: (global as any).adobeMetrics.deviceId,
      environment: (global as any).adobeMetrics.runtime.environment,
      guid: (global as any).adobeMetrics.guid,
      instanceId: (global as any).adobeMetrics.instanceId,
      payloadSequence: Payload.payloadSequence,
      runtimeVersion: (global as any).adobeMetrics.runtime.version,
      windowId: (global as any).adobeMetrics.windowId
    });
    if ((global as any).location) {
      record.metricsState.window = {location: (global as any).location};
    }
    if ((global as any).adobeMetrics.getRuntime().options.timestamp) {
      record.timestamp = record.dateNow ? new Date(record.dateNow) : new Date();
    }
  }

  public static isEmpty(): boolean {
    if (Payload.body.user.length === 0 && Payload.body.log.length === 0) {
      return true;
    }
    return false;
  }

  public static addUser(user: User, state: MetricsState): void {
    Payload.finalizeRecord(user, state);
    // check for optout cookie
    if ((global as any).document && (global as any).document.cookie && (global as any).document.cookie.search(re) > -1) {
      return;
    }
    const s = JSON.stringify(user);
    Payload.body.user.push(s);
    Payload.size += s.length;
  }

  public static addMetric(metric: Metric, state: MetricsState): void {
    const now = performance.now();
    Payload.finalizeRecord(metric, state);
    metric.offset = now - (metric.metricsState && metric.metricsState.pageState && metric.metricsState.pageState.epoch || now);
    const s = JSON.stringify(metric);
    Payload.body.log.push(s);
    Payload.size += s.length;
  }

  // Append the userCount and logCount records to Log table
  public static appendPayloadCount(reason: string): void {
    const metric = {
      counter: Payload.body.log.length + Payload.body.user.length,
      data: {log: Payload.body.log.length, reason, user: Payload.body.user.length},
      dateNow: Date.now(),
      recordType: 'PayloadCount'
    };
    Payload.finalizeRecord(metric, {sdkVersion: (global as any).adobeMetrics.getRuntime().version} as any);
    const s = JSON.stringify(metric);
    Payload.body.log.push(s);
    Payload.size += s.length;
  }

  public static toJSONString(): string {
    return '{"log":[' + Payload.body.log.join(',') + '],"user":[' + Payload.body.user.join(',') + ']}';
  }
}
